import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { PagesID } from "@utils/utilsConfigurations";
import Table from "@components/Table";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useAttachmentsAvailability } from "@hooks/useAttachmentsAvailability";
import { downloadAttachmentsZip, ServicesURL } from "@utils/utilsApi";
import { ActionsType, ColumnsType } from "../../../../interfaces/Common";
import { disableRowBasedOnDisableIf } from "@utils/utilsTable";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import CustomTabPanel from "@pages/Contents/BadgeAndCertificates/components/CustomPanel";
import {
  a11yProps,
  mapOptionsToColumns,
  mapOptionsToDataNew,
} from "@pages/Contents/BadgeAndCertificates/utils/utils";
import TaskIcon from "@mui/icons-material/Task";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import { useTranslation } from "react-i18next";
import {
  BasicTabsNewProps,
  RequestProps,
} from "@pages/Contents/BadgeAndCertificates/types";

import ButtonCst from "@components/ButtonCst";
import { useTheme } from "@mui/material";
import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import EditIcon from "@mui/icons-material/Edit";
import { ThumbDownOffAltTwoTone, ThumbUpTwoTone } from "@mui/icons-material";
import { useFilters } from "@context/filters";
import ModalCst from "@components/ModalCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalApprovalNew from "./modals/ModalApprovalNew";
import UploadModal from "../SkillBook/Enrollments/Upload/UploadModal";
import EditModal from "../SkillBook/Enrollments/Upload/EditModal";
import IconTimer from "@components/CustomIcons/IconTimer";
import DeleteModal from "../SkillBook/Enrollments/Upload/DeleteModal";
import { getCustomFieldsColumns } from "@utils/customFieldsUtils";

const BasicTabsNew: React.FC<BasicTabsNewProps> = ({
  approvedReqs,
  waitingReqs,
  rejectedReqs,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation(["skillBook-page"]);
  const { state } = location;
  const { userId } = useParams();
  const { id } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const navigate = useNavigate();
  const tabs = [
    t("waiting_requests"),
    t("approved_requests"),
    t("denied_requests"),
  ];
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [modalType, setModalType] = React.useState<"APPROVED" | "REJECTED">(
    "APPROVED",
  );
  const [selectedRequests, setSelectedRequests] = React.useState<
    RequestProps[]
  >([]);
  const [modalRequests, setModalRequests] = React.useState<RequestProps[]>([]);

  const initialValue = () => {
    const values = [state.waitingReqs, state.approvedReqs, state.rejectedReqs];
    for (let i = 0; i < values.length; i++) {
      if (values[i] !== 0) {
        return i;
      }
    }
    return 0;
  };

  const [value, setValue] = React.useState(initialValue);

  const [openEditModal, setOpenEditModal] = React.useState<
    "edit" | "upload" | "details" | "delete" | null
  >(null);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [openToast, setOpenToast] = React.useState<{
    s: StatusEnum;
    title?: string;
    message?: string;
  }>();

  const handleDownloadAttachments = async (url: string, name: string) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const result = await downloadAttachmentsZip(url, name);
    switch (result) {
      case "OK":
        setOpenToast(undefined);
        break;
      case "EMPTY":
        setOpenToast({
          s: StatusEnum.KO,
          title: t("download_attachments_empty"),
        });
        break;
      default:
        setOpenToast({
          s: StatusEnum.KO,
          message: t("download_attachments_error"),
        });
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // CHANGE TAB
    setValue(newValue);
  };

  const status = useMemo(() => {
    switch (value) {
      case 0:
        return "pending";
      case 1:
        return "approved";
      case 2:
        return "rejected";
      default:
        return "";
    }
  }, [value]);

  const config = userId
    ? "manage-requests-new"
    : "manage-requests-users-all-new";
  const filterId = `${user?.id_user}_${PagesID[config] + status}`;

  const year = useMemo(() => {
    if (!state?.year) return "";
    return `&year=${state.year}`;
  }, []);

  const {
    columns,
    dataTable,
    handleFetchData,
    actionsTopToolbar,
    actionForRow,
    mutate: getDetailTrainingRequests,
  } = useFetchTable(
    `${ServicesURL.getDetailTrainingRequests}?corporateId=${id}&status=${status}${year}${userId ? `&learnerId=${userId}` : ""}`,
    PagesID[config],
    filters[filterId],
  );

  const {
    availability: attachmentsAvailability,
    invalidate: invalidateAttachments,
    loading: attachmentsLoading,
  } = useAttachmentsAvailability(
    (dataTable?.content || []).map((request: any) => request.id),
    (requestId) =>
      ServicesURL.downloadExternalTrainingAttachments.replace(
        "externalTrainingId",
        `${requestId}`,
      ),
  );

  const { dataTable: knowledgeAreasOptions } = useFetchTable(
    `${ServicesURL.getKnowledgeAreas.replace(":idCorporate", `${id}`)}`,
    PagesID["skills.knowledge-areas"],
    { pageIndex: 0, pageSize: 100 },
  );

  const { dataTable: knowledgeDomainsOptions } = useFetchTable(
    `${ServicesURL.getKnowledgeDomainsAll.replace(":idCorporate", `${id}`)}`,
    PagesID["skills.knowledge-domains"],
  );

  const { dataTable: capabilitiesOptions } = useFetchTable(
    `${ServicesURL.getCapabilities.replace(":idCorporate", `${id}`)}`,
    PagesID["skills.capabilities"],
  );

  const { dataTable: trainingTypeOptions } = useFetchTable(
    `${ServicesURL.getCourseType}`,
    PagesID[config],
  );

  const { dataTable: totalRequests, mutate: getTotalTrainingRequests } =
    useFetchTable(
      `${ServicesURL.getTotalTrainingRequests}?corporateId=${id}&year=${state.year}`,
      PagesID[config],
      filters[filterId],
    );

  const { dataTable: totalRequestsUser, mutate: getTotalTrainingRequestsUser } =
    useFetchTable(
      userId
        ? `${ServicesURL.getTotalTrainingRequests}?corporateId=${id}&learnerId=${userId}&year=${state.year}`
        : null,
      PagesID[config],
      filters[filterId],
    );

  const optionsMap = {
    getKnowledgeAreas: knowledgeAreasOptions?.output,
    getKnowledgeDomains: knowledgeDomainsOptions?.output,
    getCapabilities: capabilitiesOptions?.output,
    getCourseTypes: trainingTypeOptions,
  };

  const allColumns: ColumnsType[] = useMemo(() => {
    const standardColumns = columns
      ? mapOptionsToColumns(columns, optionsMap)
      : [];
    const customColumns = getCustomFieldsColumns(dataTable?.content || []);
    return [...standardColumns, ...customColumns];
  }, [columns, dataTable?.content, optionsMap]);

  const updatedData: ColumnsType[] = dataTable?.content
    ? mapOptionsToDataNew(dataTable.content, optionsMap)
    : [];

  //ADD ELEMENTS selectedRequests STATE - FOR ACTION BUTTONS
  const handleOpenModal = (
    type: "APPROVED" | "REJECTED",
    requestData: RequestProps[],
  ) => {
    setModalType(type);
    setModalRequests(requestData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalRequests([]);
    setModalOpen(false);
    refreshRequests();
  };

  const refreshRequests = () => {
    invalidateAttachments();
    getDetailTrainingRequests?.();
    if (userId) {
      getTotalTrainingRequestsUser?.();
    } else {
      getTotalTrainingRequests?.();
    }
  };

  //ADD OR REMOVE ELEMENTS selectedRequests STATE - FOR CHECKBOX
  const handleSelectRequest = (row: { [key: string]: boolean }) => {
    setSelectedRequests((prevSelected: any) => {
      const newSelectedRequests = prevSelected.filter(
        (req: any) => req.id === row[req.id],
      );

      Object.keys(row).forEach((rowId) => {
        const selectedRowData = dataTable.content.find(
          (item: any) => item.id === Number(rowId),
        );
        newSelectedRequests.push(selectedRowData);
      });

      return newSelectedRequests;
    });
  };

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    // ACTION BUTTONS
    return actionForRow?.map((action: any) => {
      const approvePermission = user?.permissions.find(
        (userPermission) =>
          userPermission === "fe.external.certificate.approve",
      );
      switch (action.icon) {
        case "details":
          if (value === 2) {
            return {
              ...action,
              callBack: (row: any) => {
                if (!row.id) return;
                setSelectedId(row.id);
                setOpenEditModal("details");
              },
              getIcon: () => <RemoveRedEyeRoundedIcon />,
            };
          }
          return {};

        case "like":
          return !value
            ? {
                ...action,
                callBack: (row: any) => {
                  if (!!approvePermission) handleOpenModal("APPROVED", [row]);
                },
                getIcon: () => {
                  if (!!approvePermission) return <ThumbUpIcon />;
                  return (
                    <Box>
                      <ThumbUpTwoTone color={"disabled"} />
                    </Box>
                  );
                },
              }
            : {};
        case "dislike":
          return !value
            ? {
                ...action,
                callBack: (row: any) => {
                  if (!!approvePermission) handleOpenModal("REJECTED", [row]);
                },
                getIcon: () => {
                  if (!!approvePermission) return <ThumbDownIcon />;
                  return (
                    <Box>
                      <ThumbDownOffAltTwoTone color={"disabled"} />
                    </Box>
                  );
                },
              }
            : {};
        case "edit":
          return value === 0 || value === 1
            ? {
                ...action,
                callBack: (row: any) => {
                  if (!row.id) return;
                  setSelectedId(row.id);
                  setOpenEditModal("edit");
                },
                getIcon: (row: any) => {
                  return <EditIcon />;
                },
              }
            : {};

        case "download-attachments":
          return {
            ...action,
            callBack: (row: any) => {
              if (!row?.id || !attachmentsAvailability[row.id]) return;
              handleDownloadAttachments(
                ServicesURL.downloadExternalTrainingAttachments.replace(
                  "externalTrainingId",
                  `${row.id}`,
                ),
                `attachments_${row.id}`,
              );
            },
            getIcon: (row: any) => {
              if (!attachmentsAvailability[row?.id])
                return (
                  <Box>
                    <FolderZipIcon color={"disabled"} />
                  </Box>
                );
              return <FolderZipIcon />;
            },
          };

        case "upload":
          return value === 1
            ? {
                ...action,
                callBack: (row: any) => {
                  if (!row.id) return;
                  setSelectedId(row.id);
                  setOpenEditModal("upload");
                },
                getIcon: (row: any) => {
                  return <TaskIcon />;
                },
              }
            : {};
        case "delete":
          return value === 1 || value === 2
            ? {
                ...action,
                callBack: (row: any) => {
                  if (!row) return;
                  setSelectedId(row);
                  setOpenEditModal("delete");
                },
                getIcon: () => {
                  if (!!approvePermission) return <ThumbDownIcon />;
                  return (
                    <Box>
                      <ThumbDownOffAltTwoTone color={"disabled"} />
                    </Box>
                  );
                },
              }
            : {};

        default:
          return action;
      }
    });
  }, [actionForRow, attachmentsAvailability]);

  const selectedRows = selectedRequests.reduce((acc: any, item) => {
    acc[item.id] = true;
    return acc;
  }, {});

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    configRowNew = disableRowBasedOnDisableIf(configRowNew, row);
    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          {tabs &&
            tabs.map((item: string) => {
              let disabled = false;
              let icon = null;
              let label = null;
              switch (item) {
                case t("waiting_requests"):
                  disabled = !(userId
                    ? totalRequestsUser?.pendingRequests
                    : totalRequests?.pendingRequests);
                  icon = <IconTimer />;
                  label = `${item} - ${userId ? totalRequestsUser?.pendingRequests : (totalRequests?.pendingRequests ?? 0)}`;
                  break;
                case t("approved_requests"):
                  disabled = !(userId
                    ? totalRequestsUser?.approvedRequests
                    : totalRequests?.approvedRequests);
                  icon = <CheckCircleRoundedIcon />;
                  label = `${item} - ${userId ? totalRequestsUser?.approvedRequests : (totalRequests?.approvedRequests ?? 0)}`;
                  break;
                case t("denied_requests"):
                  disabled = !(userId
                    ? totalRequestsUser?.rejectedRequests
                    : totalRequests?.rejectedRequests);
                  icon = <CancelRoundedIcon />;
                  label = `${item} - ${userId ? totalRequestsUser?.rejectedRequests : (totalRequests?.rejectedRequests ?? 0)}`;
                  break;
              }
              return (
                <Tab
                  label={label}
                  icon={icon as React.ReactElement}
                  iconPosition="start"
                  sx={{
                    color: theme.palette.text.primary,
                    transition: "color 0.3s",
                    "&.Mui-selected": {
                      color: theme.palette.status.in_progress,
                    },
                  }}
                  {...a11yProps(0)}
                  disabled={disabled}
                />
              );
            })}
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <Table
          id={PagesID[config] + status}
          data={
            // Table mostra il loader quando data è assente
            attachmentsLoading
              ? (undefined as unknown as ColumnsType[])
              : updatedData || []
          }
          columns={allColumns || []}
          configurationTopToolbar={actionsTopToolbar}
          configurationActionForRow={configurationActionForRow}
          renderRowActions={customRenderRowActions}
          totalPages={dataTable?.metadata?.totalPages}
          enableRowSelection={true}
          enableSelectAll={true}
          getRowId={(row: any) => (row ? row.id : null)}
          onRowSelectionChange={(row: any) => handleSelectRequest(row)}
          selectedRows={selectedRows}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <Table
          id={PagesID[config] + status}
          data={attachmentsLoading ? undefined : dataTable?.content || []}
          columns={allColumns || []}
          configurationTopToolbar={actionsTopToolbar}
          configurationActionForRow={configurationActionForRow}
          renderRowActions={customRenderRowActions}
          totalPages={dataTable?.metadata?.totalPages}
          enableRowSelection={false}
          enableSelectAll={false}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Table
          id={PagesID[config] + status}
          data={attachmentsLoading ? undefined : dataTable?.content || []}
          columns={allColumns || []}
          configurationTopToolbar={actionsTopToolbar}
          configurationActionForRow={configurationActionForRow}
          renderRowActions={customRenderRowActions}
          totalPages={dataTable?.metadata?.totalPages}
          enableRowSelection={false}
          enableSelectAll={false}
        />
      </CustomTabPanel>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          my: 4,
          paddingTop: "30px",
        }}
      >
        <ButtonCst
          id={"confirmBtn"}
          color="primary"
          onClick={() => handleOpenModal("REJECTED", selectedRequests)}
          sx={{ mx: "5px" }}
          disabled={selectedRequests.length === 0}
        >
          {t("disapprove_selezionate")}
        </ButtonCst>
        <ButtonCst
          id={"denyBtn"}
          variant="contained"
          color="primary"
          onClick={() => handleOpenModal("APPROVED", selectedRequests)}
          sx={{ mx: "5px" }}
          disabled={selectedRequests.length === 0}
        >
          {t("approve_selected")}
        </ButtonCst>
      </Box>
      {modalRequests.length > 0 && (
        <ModalApprovalNew
          type={modalType}
          requestData={modalRequests}
          open={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
      {openEditModal === "edit" && selectedId && (
        <ModalCst
          open={true}
          setOpen={() => setOpenEditModal(null)}
          title="Modifica Informazioni"
          typeOfModal=""
          noToast
        >
          <EditModal
            externalTrainingId={selectedId}
            onSuccess={() => (setOpenEditModal(null), refreshRequests())}
          />
        </ModalCst>
      )}

      {openEditModal === "upload" && selectedId && (
        <ModalCst
          open={true}
          setOpen={() => setOpenEditModal(null)}
          title="Upload Documenti"
          typeOfModal=""
          noToast
        >
          <UploadModal
            externalTrainingId={selectedId}
            onSuccess={() => (setOpenEditModal(null), refreshRequests())}
          />
        </ModalCst>
      )}

      {openEditModal === "details" && selectedId && (
        <ModalCst
          open={true}
          setOpen={() => setOpenEditModal(null)}
          title="Dettagli Corso"
          typeOfModal=""
          noToast
        >
          <EditModal
            externalTrainingId={selectedId}
            onSuccess={() => (setOpenEditModal(null), refreshRequests())}
            onlyVisualization={true}
          />
        </ModalCst>
      )}

      {openEditModal === "delete" && selectedId && (
        <ModalCst
          open={true}
          setOpen={() => setOpenEditModal(null)}
          title="Elimina Richiesta"
          typeOfModal=""
          noToast
        >
          <DeleteModal
            externalTrainingId={selectedId}
            onSuccess={() => (setOpenEditModal(null), refreshRequests())}
          />
        </ModalCst>
      )}
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => setOpenToast(undefined)}
        status={openToast}
      />
    </Box>
  );
};

export default BasicTabsNew;
